import { memo, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  DataTableVirtualized,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
  Spinner,
  ToggleGroup,
  ToggleGroupItem,
  useDataTable
} from 'design-system';

import type { TimeRangeButton } from '@/config/constants';
import { TABLE_ROW_HEIGHT_PX } from '@/config/constants';
import { Permission } from '@/entities/permissions';
import type { DateFormattingConfig } from '@/entities/user-config';
import { usePermission, useUserConfig } from '@/features/_shared/user';
import useCsvDownload from '@/features/charts/hooks/useCsvDownload';

import { csvTableOptions } from './api/csvTable.options';
import type { CsvTableData, WorkerMessage, WorkerResponse } from './api/csvTable.transform';
import { createCsvTableWorker } from './api/csvTable.workerFactory';
import {
  getCsvTableColumns,
  type CsvColumnAlignments,
  type CsvTableDataRow
} from './csvTableColumns';

const TABLE_HEADER_HEIGHT_PX = 47;
const DEFAULT_VISIBLE_ROW_COUNT = 20;

export type CSVTableMetadata = {
  queryFileId?: string;
  timeRangeButtons?: TimeRangeButton[];
  align?: CsvColumnAlignments;
};

type CsvTableWidgetProps = {
  fileName: string;
  groupName: string;
  layout?: { fullWidth?: boolean; height?: number };
  metadata?: CSVTableMetadata;
};

type CsvTableContentProps = {
  from: string | undefined;
  fileName: string;
  groupName: string;
  queryFileId: string | undefined;
  columnAlignments: CsvColumnAlignments;
  layout: CsvTableWidgetProps['layout'];
};

const getRowId = (row: CsvTableDataRow) => row.id;

function CsvTableLoading() {
  return (
    <div
      aria-label='Loading CSV table'
      className='space-y-2 p-4'
    >
      <Skeleton className='h-8 w-full' />
      <Skeleton className='h-8 w-full' />
      <Skeleton className='h-8 w-full' />
    </div>
  );
}

function CsvTableEmpty() {
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>No data available for the selected date range.</EmptyTitle>
        <EmptyDescription>Try adjusting it.</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function CsvTableError({ processing = false }: { processing?: boolean }) {
  return (
    <Alert variant='destructive'>
      <AlertTitle>CSV data unavailable</AlertTitle>
      <AlertDescription>
        {processing ? 'Failed to process CSV data.' : 'Failed to load data.'}
      </AlertDescription>
    </Alert>
  );
}

const CsvTableContent = memo(
  ({ from, fileName, groupName, queryFileId, columnAlignments, layout }: CsvTableContentProps) => {
    const { hasPermission } = usePermission();
    const { data: userConfigData } = useUserConfig();
    const dateFormattingConfig = useMemo<DateFormattingConfig>(
      () => ({
        dateFormat: userConfigData?.dateFormat.value ?? 'dd-MMM-yy',
        timezone: userConfigData?.timezone.value ?? 'UTC'
      }),
      [userConfigData?.dateFormat.value, userConfigData?.timezone.value]
    );

    const {
      data: rawText,
      isLoading,
      error
    } = useQuery({
      ...csvTableOptions.data({ fileName, groupName, queryFileId, from }),
      enabled: hasPermission(Permission.ViewMarketScreens)
    });

    const [csvData, setCsvData] = useState<CsvTableData | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [workerError, setWorkerError] = useState<string | null>(null);
    const workerRef = useRef<ReturnType<typeof createCsvTableWorker> | null>(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
      const worker = createCsvTableWorker();
      worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
        if (event.data.requestId !== requestIdRef.current) return;

        setIsProcessing(false);
        if (event.data.error !== undefined) {
          setWorkerError(event.data.error);
          return;
        }

        setWorkerError(null);
        setCsvData(event.data.data);
      };
      workerRef.current = worker;

      return () => {
        requestIdRef.current += 1;
        worker.terminate();
        workerRef.current = null;
      };
    }, []);

    useEffect(() => {
      const requestId = ++requestIdRef.current;
      if (!workerRef.current || rawText === undefined) return;

      if (!rawText.trim()) {
        // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- Empty input bypasses the external worker, so its prior result must be cleared at this synchronization boundary.
        setCsvData(null);
        setIsProcessing(false);
        setWorkerError(null);
        return;
      }

      setIsProcessing(true);
      workerRef.current.postMessage({
        requestId,
        text: rawText,
        userConfig: dateFormattingConfig
      } satisfies WorkerMessage);
    }, [rawText, dateFormattingConfig]);

    const { headerColumns, formattedDataRows, dateColumnKey } = csvData ?? {
      headerColumns: [],
      formattedDataRows: [],
      dateColumnKey: null
    };
    const columns = useMemo(
      () => getCsvTableColumns(headerColumns, columnAlignments),
      [columnAlignments, headerColumns]
    );
    const table = useDataTable({
      columns,
      data: formattedDataRows,
      getRowId
    });

    useCsvDownload(from, queryFileId, { fileName, groupName, dateColumnKey });

    const maxHeight = layout?.height
      ? layout.height * TABLE_ROW_HEIGHT_PX + TABLE_HEADER_HEIGHT_PX
      : Math.min(formattedDataRows.length, DEFAULT_VISIBLE_ROW_COUNT) * TABLE_ROW_HEIGHT_PX +
        TABLE_HEADER_HEIGHT_PX;
    const isRefreshing = Boolean(csvData) && (isLoading || isProcessing);

    if (!csvData && isLoading) return <CsvTableLoading />;
    if (error) return <CsvTableError />;
    if (workerError) return <CsvTableError processing />;
    if (!csvData && isProcessing) {
      return (
        <div className='flex min-h-24 items-center justify-center'>
          <Spinner
            aria-label='Processing CSV data'
            className='size-8'
          />
        </div>
      );
    }
    if (!csvData || formattedDataRows.length === 0) return <CsvTableEmpty />;

    return (
      <div className='relative min-w-0'>
        {isRefreshing ? (
          <div className='absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-background/70'>
            <Spinner
              aria-label='Refreshing CSV data'
              className='size-8'
            />
          </div>
        ) : null}
        <section
          aria-label='CSV table viewport'
          className='min-w-0'
          data-max-height={maxHeight}
          style={{ height: maxHeight, maxHeight }}
        >
          <DataTableVirtualized
            aria-label='CSV market data'
            estimateSize={TABLE_ROW_HEIGHT_PX}
            table={table}
            virtualizeColumns={headerColumns.length > 12}
          />
        </section>
      </div>
    );
  }
);

const CsvTableWidget = ({ fileName, groupName, metadata, layout }: CsvTableWidgetProps) => {
  const { hasPermission } = usePermission();
  const timeRangeButtons = useMemo(
    () => metadata?.timeRangeButtons ?? [],
    [metadata?.timeRangeButtons]
  );
  const columnAlignments = useMemo<CsvColumnAlignments>(
    () => metadata?.align ?? {},
    [metadata?.align]
  );
  const [from, setFrom] = useState<string | undefined>();
  const queryFileId = metadata?.queryFileId;
  const defaultFrom = useMemo(
    () => timeRangeButtons.find(button => button.default)?.value,
    [timeRangeButtons]
  );
  const effectiveFrom = from ?? defaultFrom;

  const handleRangeChange = useCallback(
    (values: string[]) => {
      const value = values.at(-1);
      if (!value || value === effectiveFrom) return;
      startTransition(() => {
        setFrom(value);
      });
    },
    [effectiveFrom]
  );

  if (!hasPermission(Permission.ViewMarketScreens)) {
    return (
      <Alert>
        <AlertTitle>Restricted access</AlertTitle>
        <AlertDescription>Upgrade your plan to view market data.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className='min-w-0 space-y-2'>
      {timeRangeButtons.length > 0 ? (
        <ToggleGroup
          aria-label='CSV table time range'
          spacing={0}
          value={effectiveFrom ? [effectiveFrom] : []}
          variant='outline'
          onValueChange={handleRangeChange}
        >
          {timeRangeButtons.map(button => (
            <ToggleGroupItem
              key={button.value}
              aria-label={button.label}
              value={button.value}
            >
              {button.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      ) : null}
      <CsvTableContent
        columnAlignments={columnAlignments}
        fileName={fileName}
        from={effectiveFrom}
        groupName={groupName}
        layout={layout}
        queryFileId={queryFileId}
      />
    </div>
  );
};

export default CsvTableWidget;
